import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { profile, education } from '../data/mock';
import { MapPin, GraduationCap, Target, Compass, UserRound, User } from 'lucide-react';

const About = () => {
  const { t, language } = useLanguage();
  const interestsList = t('about.interestsList');

  return (
    <div>
      {/* Header */}
      <section className="relative border-b border-white/10 sky-section overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] cfd-bar" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
          <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#E4A135] mb-3">
            <User className="w-4 h-4" />
            <span className="w-8 h-px bg-[#E4A135]" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 tracking-tight">
            {t('about.title')}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
            {t('about.subtitle')}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20 space-y-16">

        {/* 01 — Profile (name, title, tagline, bio) */}
        <section data-testid="about-section-profile">
          <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4A8FD1] mb-3">
            <span>01</span>
            <UserRound className="w-4 h-4" />
            <span className="w-8 h-px bg-[#4A8FD1]" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-50 mb-6">
            {t('about.profile')}
          </h2>

          <div className="bg-[#1A2231] border border-white/10 rounded-lg p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5 mb-6">
              <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-[#111827] to-[#4A8FD1] text-white flex items-center justify-center mono font-bold text-xl tracking-wider flex-shrink-0">
                SVM
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-semibold text-slate-50">
                  {profile.name}
                </h3>
                <p className="text-sm sm:text-base text-[#4A8FD1] font-medium mt-1">
                  {profile.role[language]}
                </p>
                <div className="flex items-center gap-2 text-slate-400 mt-2">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">{profile.location[language]}</span>
                </div>
              </div>
            </div>

            <p className="text-slate-300 leading-relaxed text-base">
              {profile.tagline[language]}
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed text-base">
              {profile.shortBio[language]}
            </p>
          </div>
        </section>

        {/* 02 — Career Objective */}
        <section data-testid="about-section-objective">
          <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4CB88A] mb-3">
            <span>02</span>
            <Target className="w-4 h-4" />
            <span className="w-8 h-px bg-[#4CB88A]" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-50 mb-6">
            {t('about.objective')}
          </h2>
          <div className="p-6 sm:p-8 bg-white/5 border-l-4 border-[#4CB88A] rounded-r-lg">
            <p className="text-slate-200 leading-relaxed text-base sm:text-lg">
              {t('about.objectiveText')}
            </p>
          </div>
        </section>

        {/* 03 — Education */}
        <section data-testid="about-section-education">
          <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4BADC8] mb-3">
            <span>03</span>
            <GraduationCap className="w-4 h-4" />
            <span className="w-8 h-px bg-[#4BADC8]" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-50 mb-6">
            {t('about.education')}
          </h2>
          <div className="space-y-5">
            {education.map((e) => (
              <div key={e.id} className="p-5 bg-[#1A2231] border border-white/10 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-slate-50">
                      {e.degree[language]}
                    </h3>
                    <p className="text-sm text-slate-400 mt-0.5">
                      {e.institution[language]}
                    </p>
                  </div>
                  <span className="text-xs mono uppercase tracking-wider text-slate-500 whitespace-nowrap">
                    {e.period}
                  </span>
                </div>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                  {e.details[language]}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* 04 — Engineering Interests */}
        <section data-testid="about-section-interests">
          <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#E4A135] mb-3">
            <span>04</span>
            <Compass className="w-4 h-4" />
            <span className="w-8 h-px bg-[#E4A135]" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-50 mb-6">
            {t('about.interests')}
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.isArray(interestsList) &&
              interestsList.map((item, idx) => (
                <li
                  key={idx}
                  className="p-4 bg-[#1A2231] border border-white/10 rounded-lg flex items-start gap-3"
                >
                  <span className="mono text-xs text-[#E4A135] font-semibold pt-0.5">
                    0{idx + 1}
                  </span>
                  <span className="text-sm text-slate-300 leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
          </ul>
        </section>

      </div>
    </div>
  );
};

export default About;
